import {
  TreeItemGroupTransition,
  TreeItemProps,
  TreeItemProvider,
  useTreeItem,
  useTreeItemUtils,
} from '@mui/x-tree-view';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import {
  goalInboxID,
  lifeAreaOtherID,
  overdueSectionID,
  unlinkedPlannedTasksID,
} from 'shared/types/orderable-section';
import { Task } from 'shared/types/task';
import { createInboxGoal, GoalTypes } from 'shared/utils/create-inbox-goal';
import {
  createOtherLifeArea,
  LifeAreaTypes,
} from 'shared/utils/create-other-life-area';
import { parseOverdueId } from 'shared/utils/format-overdue-id';

import * as Styled from './custom-tree-item.style';
import { GoalCard } from './goal-card';
import { GoalSectionCard } from './goal-section-card';
import { LifeAreaCard } from './life-area-card';
import { TaskCard } from './task-card';

enum ComponentToRender {
  Task = 'task',
  GoalSection = 'goal-section',
  Goal = 'goal',
  LifeArea = 'life-area',
  Container = 'container',
}

export type CustomTreeItemProps = TreeItemProps & {
  idDataMap: Record<ID, LifeArea | Goal | Task>;
  lifeAreas: LifeArea[];
  goals: Goal[];
  onCollapse?: (id: ID) => void;
  hasDnd?: boolean;
  preventDnd?: boolean;
  hideTaskDurationInteraction?: boolean;
};

export const CustomTreeItem = React.forwardRef<
  HTMLLIElement,
  CustomTreeItemProps
>(
  (
    {
      id,
      itemId,
      children,
      label,
      disabled,
      idDataMap,
      lifeAreas,
      goals,
      onCollapse,
      hasDnd,
      preventDnd,
      hideTaskDurationInteraction,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const {
      getRootProps,
      getContentProps,
      getGroupTransitionProps,
      getContextProviderProps,
      status,
    } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });

    const { interactions } = useTreeItemUtils({
      itemId,
      children,
    });

    const { data, parent } = useMemo(() => {
      const _itemId = parseOverdueId(itemId);
      const data = idDataMap[_itemId];

      if (data) {
        return { data };
      }

      // inbox
      if (itemId === goalInboxID) {
        return { data: createInboxGoal(t) };
      }

      // overdue
      if (itemId === overdueSectionID) {
        return { data: createInboxGoal(t, GoalTypes.Overdue) };
      }

      // unplanned
      if (itemId === unlinkedPlannedTasksID) {
        return { data: createInboxGoal(t, GoalTypes.Scheduled) };
      }

      // more
      if (itemId === lifeAreaOtherID) {
        return { data: createOtherLifeArea(t, LifeAreaTypes.More) };
      }

      // sections
      const parent = goals.find((goal) =>
        goal.sections?.some(({ id }) => id === itemId),
      );
      const sectionData = parent?.sections?.find(({ id }) => id === itemId);

      return { parent, data: sectionData };
    }, [goals, idDataMap, itemId, t]);

    const _onCollapse = (e: React.MouseEvent) => {
      interactions.handleExpansion(e);
      onCollapse?.(itemId);
    };

    if (!data) {
      return null;
    }

    const componentRenderType =
      'type' in data
        ? ComponentToRender.Task
        : 'sections' in data
          ? ComponentToRender.Goal
          : 'tasks' in data
            ? ComponentToRender.GoalSection
            : ComponentToRender.LifeArea;

    return (
      <TreeItemProvider {...getContextProviderProps()}>
        <Styled.MuiTreeItemRoot
          {...getRootProps()}
          $borderTop={data.id === lifeAreaOtherID}
          $borderBottom={data.id === goalInboxID}
        >
          <Styled.MuiTreeItemContent {...getContentProps()} onClick={undefined}>
            <Styled.CustomItemContainer
              $hasBorderBottom={
                status.expanded &&
                [
                  ComponentToRender.GoalSection,
                  ComponentToRender.Goal,
                ].includes(componentRenderType)
              }
            >
              {componentRenderType === ComponentToRender.Task && (
                <TaskCard
                  task={data as Task}
                  hasDnd={hasDnd}
                  preventDnd={preventDnd}
                  isGrabbable={hasDnd}
                  hideDurationInteraction={hideTaskDurationInteraction}
                  hideGoal
                  hideLifeArea
                />
              )}

              {componentRenderType === ComponentToRender.GoalSection && (
                <GoalSectionCard
                  goal={parent!}
                  id={itemId}
                  onCollapse={status.expandable ? _onCollapse : undefined}
                  collapsed={status.expanded}
                  // hasDnd={hasDnd}
                  // preventDnd={preventDnd}
                />
              )}

              {componentRenderType === ComponentToRender.Goal && (
                <GoalCard
                  lifeAreas={lifeAreas}
                  goals={goals}
                  goal={data as Goal}
                  onCollapse={status.expandable ? _onCollapse : undefined}
                  collapsed={status.expanded}
                  hasDnd={hasDnd}
                  preventDnd={preventDnd}
                  isGrabbable={hasDnd}
                  childCount={(children as React.ReactNode[])?.length}
                />
              )}

              {componentRenderType === ComponentToRender.LifeArea && (
                <LifeAreaCard
                  lifeArea={data as LifeArea}
                  onCollapse={status.expandable ? _onCollapse : undefined}
                  collapsed={status.expanded}
                  hasDnd={hasDnd}
                  preventDnd={preventDnd}
                  isGrabbable={hasDnd}
                />
              )}
            </Styled.CustomItemContainer>
          </Styled.MuiTreeItemContent>

          {children && (
            <TreeItemGroupTransition {...getGroupTransitionProps()} />
          )}
        </Styled.MuiTreeItemRoot>
      </TreeItemProvider>
    );
  },
);
